/**
 * t352u: Settings › Equipment categories (desktop). Add, rename, reorder and archive the workspace's categories; the
 * register groups and filters by them. Same permission as managing equipment. The phone pages are
 * mobile/settings/WorkspaceSettings (web) and apps/mobile settings/equipmentCategories (app).
 */
import { useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Archive, ArchiveRestore, Pencil, Plus } from 'lucide-react';
import { EQUIPMENT_CATEGORIES_NOTE, equipmentCategoryGroups, equipmentCategoryOrder, equipmentCategoryProblem, type EquipmentCategory } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { DialogFooter, Button, Card, Field, Input, Modal, Spinner, ask } from '@/components/ui';
import { opsKeys } from './common';

export const equipmentCategoriesKey = (ws: string) => opsKeys(ws, 'equipment', 'categories');
export function useEquipmentCategories() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: equipmentCategoriesKey(ws ?? ''), queryFn: () => api.ops.equipmentCategories(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}
const count = (c: EquipmentCategory) => `${c.item_count} item${c.item_count === 1 ? '' : 's'}`;

export function EquipmentCategoriesPanel() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useEquipmentCategories();
  const all = q.data ?? [];
  const g = equipmentCategoryGroups(all);
  const [edit, setEdit] = useState<{ c: EquipmentCategory | null; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast(ok, 'success'); void queryClient.invalidateQueries({ queryKey: opsKeys(ws ?? '', 'equipment') }); return true; }
    catch (e) { toast(errorMessage(e, 'Couldn’t save the category'), 'error'); return false; }
    finally { setBusy(false); }
  };
  const problem = edit ? equipmentCategoryProblem(edit.name, all, edit.c?.id) : null;
  const save = async () => {
    if (!edit || problem) return;
    const name = edit.name.trim();
    if (await run(() => (edit.c ? api.ops.updateEquipmentCategory(ws!, edit.c.id, { name }) : api.ops.createEquipmentCategory(ws!, { name })), edit.c ? 'Category renamed' : 'Category added')) setEdit(null);
  };
  const archive = async (c: EquipmentCategory) => {
    if (c.archived_at) return void run(() => api.ops.updateEquipmentCategory(ws!, c.id, { archived: false }), `${c.name} restored`);
    if (await ask({ title: `Archive “${c.name}”?`, body: c.item_count ? `Its ${count(c)} keep it, but it can’t be picked for other equipment.` : 'It can’t be picked any more. You can restore it later.', confirmLabel: 'Archive' })) {
      void run(() => api.ops.updateEquipmentCategory(ws!, c.id, { archived: true }), `${c.name} archived`);
    }
  };
  const Row = ({ c, i }: { c: EquipmentCategory; i?: number }) => (
    <li className={clsx('flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700', c.archived_at && 'bg-slate-50 dark:bg-slate-900')} data-testid="equipment-category-row">
      {i !== undefined && <span className="flex flex-col">
        <button type="button" aria-label={`Move ${c.name} up`} disabled={busy || i === 0} onClick={() => void run(() => api.ops.reorderEquipmentCategories(ws!, equipmentCategoryOrder(all, c.id, -1)), 'Order saved')} className="rounded p-0.5 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowUp className="h-3.5 w-3.5" /></button>
        <button type="button" aria-label={`Move ${c.name} down`} disabled={busy || i === g.live.length - 1} onClick={() => void run(() => api.ops.reorderEquipmentCategories(ws!, equipmentCategoryOrder(all, c.id, 1)), 'Order saved')} className="rounded p-0.5 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowDown className="h-3.5 w-3.5" /></button>
      </span>}
      <span className={clsx('min-w-0 flex-1 truncate text-sm font-medium', c.archived_at && 'text-slate-500')}>{c.name}</span>
      <span className="text-xs text-slate-500">{count(c)}{c.archived_at ? ' · archived' : ''}</span>
      {!c.archived_at && <Button size="icon" variant="ghost" aria-label={`Rename ${c.name}`} onClick={() => setEdit({ c, name: c.name })}><Pencil className="h-4 w-4" /></Button>}
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void archive(c)} data-testid="equipment-category-archive">
        {c.archived_at ? <><ArchiveRestore className="h-4 w-4" />Restore</> : <><Archive className="h-4 w-4" />Archive</>}</Button>
    </li>
  );
  return (
    <>
      <Card title="Equipment categories" actions={<Button size="sm" onClick={() => setEdit({ c: null, name: '' })} data-testid="equipment-category-add"><Plus className="h-4 w-4" />Add category</Button>}>
        <p className="mb-3 text-sm text-slate-500">{EQUIPMENT_CATEGORIES_NOTE} The register lists them in this order, with Uncategorised last.</p>
        {q.isLoading ? <Spinner /> : q.error ? <p className="text-sm text-rose-700">{errorMessage(q.error)}</p> : <>
          {!g.live.length && <p className="text-sm text-slate-500">No categories yet.</p>}
          <ul className="space-y-2" aria-label="Equipment categories" data-testid="equipment-categories">{g.live.map((c, i) => <Row key={c.id} c={c} i={i} />)}</ul>
          {g.archived.length > 0 && <>
            <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">Archived</h3>
            <ul className="space-y-2">{g.archived.map(c => <Row key={c.id} c={c} />)}</ul>
          </>}
        </>}
      </Card>
      {edit && <Modal open onClose={() => setEdit(null)} title={edit.c ? `Rename ${edit.c.name}` : 'Add a category'}
        footer={<DialogFooter onCancel={() => setEdit(null)} onSave={() => void save()} saving={busy} disabled={Boolean(problem)} saveTestId="equipment-category-save" />}>
        <Field label="Name" hint={edit.name.trim() && problem ? <span className="text-rose-700 dark:text-rose-300" role="status">{problem}</span> : undefined}>
          <Input autoFocus value={edit.name} maxLength={60} placeholder="e.g. Cleaning machines" onChange={e => setEdit({ ...edit, name: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') void save(); }} data-testid="equipment-category-name" /></Field>
      </Modal>}
    </>
  );
}
